/**
 * Add or rename someone at the table. A name, and nothing else.
 *
 * No age, no birthday, no dietary profile — every extra field is one more
 * thing to fill in before you can answer "who liked the gratin?", and none of
 * them change the answer.
 *
 * Removing does not touch past verdicts. They ate the food and they had an
 * opinion; deleting it would quietly rewrite the household's history of a
 * recipe, which is a worse outcome than a name that no longer renders.
 */
import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  Modal,
  Pressable,
  TextInput,
  StyleSheet,
  Platform,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
  PaprikaShadows,
} from '../../styles/theme';
import { EATER_NAME_MAX, normalizeEaterName, type Eater } from '../../services/eatersService';

const isWeb = Platform.OS === 'web';

interface Props {
  visible: boolean;
  /** null when adding someone new. */
  eater: Eater | null;
  onSubmit: (name: string) => void | Promise<void>;
  onRemove?: () => void | Promise<void>;
  onClose: () => void;
}

export function EaterEditSheet({ visible, eater, onSubmit, onRemove, onClose }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState(eater?.name ?? '');
  const [busy, setBusy] = useState(false);

  // One sheet is reused for every row, so the field has to follow whichever
  // person was tapped rather than keeping the first one's name.
  useEffect(() => {
    setName(eater?.name ?? '');
    setBusy(false);
  }, [eater, visible]);

  const valid = normalizeEaterName(name).length > 0;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    await onSubmit(name);
  };

  const remove = async () => {
    if (!onRemove || busy) return;
    setBusy(true);
    await onRemove();
  };

  return (
    <Modal visible={visible} transparent animationType={isWeb ? 'fade' : 'slide'} onRequestClose={onClose}>
      <Pressable style={isWeb ? styles.overlayWeb : styles.overlay} onPress={onClose}>
        <Pressable style={isWeb ? styles.sheetWeb : styles.sheet} onPress={(e) => e.stopPropagation()}>
          {!isWeb && (
            <View style={styles.handleWrap}>
              <View style={styles.handle} />
            </View>
          )}

          <View style={styles.content}>
            <Text style={styles.title}>
              {eater ? eater.name : t('household.eaters.addTitle')}
            </Text>

            <Text style={styles.label}>{t('household.eaters.firstName')}</Text>
            <TextInput
              style={styles.input}
              value={name}
              onChangeText={setName}
              placeholder={t('household.eaters.namePlaceholder')}
              placeholderTextColor={Paprika.textMuted}
              maxLength={EATER_NAME_MAX}
              autoFocus
              returnKeyType="done"
              onSubmitEditing={submit}
              accessibilityLabel={t('household.eaters.firstName')}
            />

            <View style={styles.actions}>
              <Pressable style={styles.cancel} onPress={onClose} accessibilityRole="button">
                <Text style={styles.cancelText}>{t('household.eaters.cancel')}</Text>
              </Pressable>
              <Pressable
                style={[styles.confirm, !valid && styles.confirmDisabled]}
                onPress={submit}
                disabled={!valid || busy}
                accessibilityRole="button"
              >
                <Text style={styles.confirmText}>
                  {eater ? t('household.eaters.save') : t('household.eaters.add')}
                </Text>
              </Pressable>
            </View>

            {onRemove && (
              <View style={styles.removeBlock}>
                <Pressable onPress={remove} disabled={busy} accessibilityRole="button">
                  <Text style={styles.removeText}>{t('household.eaters.remove')}</Text>
                </Pressable>
                <Text style={styles.removeNote}>{t('household.eaters.removeNote')}</Text>
              </View>
            )}
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(27, 28, 26, 0.35)',
    justifyContent: 'flex-end',
  },
  overlayWeb: {
    flex: 1,
    backgroundColor: 'rgba(27, 28, 26, 0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  sheet: {
    backgroundColor: Paprika.surfaceLowest,
    borderTopLeftRadius: PaprikaRadius.xl,
    borderTopRightRadius: PaprikaRadius.xl,
  },
  sheetWeb: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.lg,
    width: 380,
    maxWidth: '92%',
    ...PaprikaShadows.card,
  },
  handleWrap: { alignItems: 'center', paddingTop: PaprikaSpacing.sm },
  handle: { width: 36, height: 4, borderRadius: 2, backgroundColor: Paprika.border },
  content: { padding: PaprikaSpacing.md, gap: PaprikaSpacing.sm },
  title: {
    ...PaprikaTypography.titleMedium,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '600',
    color: Paprika.textPrimary,
  },
  label: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: PaprikaSpacing.xs,
  },
  input: {
    ...PaprikaTypography.bodyLarge,
    color: Paprika.textPrimary,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.md,
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: 12,
  },
  actions: {
    flexDirection: 'row',
    gap: PaprikaSpacing.sm,
    marginTop: PaprikaSpacing.sm,
  },
  cancel: {
    flex: 1,
    paddingVertical: 13,
    borderRadius: PaprikaRadius.md,
    alignItems: 'center',
    backgroundColor: Paprika.surfaceLow,
  },
  cancelText: { ...PaprikaTypography.labelLarge, color: Paprika.textSecondary },
  confirm: {
    flex: 1,
    paddingVertical: 13,
    borderRadius: PaprikaRadius.md,
    alignItems: 'center',
    backgroundColor: Paprika.accent,
  },
  confirmDisabled: { opacity: 0.45 },
  confirmText: { ...PaprikaTypography.labelLarge, color: '#ffffff' },
  removeBlock: { marginTop: PaprikaSpacing.sm, gap: 2 },
  removeText: { ...PaprikaTypography.labelLarge, color: Paprika.approvalNo },
  removeNote: { ...PaprikaTypography.caption, color: Paprika.textMuted },
});
